import type { CSSProperties } from "react";

import { WorkspacePrimaryColorSchema } from "../../shared/schemas";

const LIGHT_BACKGROUND = "#fafbfc";
const DARK_BACKGROUND = "#0b1117";
const BLACK = "#000000";
const WHITE = "#ffffff";
// Leave a small margin so eight-bit RGB rounding cannot slip below WCAG AA.
const MINIMUM_CONTRAST = 4.6;

type Rgb = { red: number; green: number; blue: number };

type WorkspaceAccentStyle = CSSProperties &
  Record<
    | "--workspace-accent-light"
    | "--workspace-accent-light-hover"
    | "--workspace-accent-light-deep"
    | "--workspace-accent-light-ink"
    | "--workspace-accent-light-link"
    | "--workspace-accent-light-info"
    | "--workspace-accent-light-focus"
    | "--workspace-accent-dark"
    | "--workspace-accent-dark-hover"
    | "--workspace-accent-dark-deep"
    | "--workspace-accent-dark-ink"
    | "--workspace-accent-dark-link"
    | "--workspace-accent-dark-info"
    | "--workspace-accent-dark-focus",
    string
  >;

function parseHex(color: string): Rgb {
  const value = WorkspacePrimaryColorSchema.parse(color).slice(1);
  return {
    red: Number.parseInt(value.slice(0, 2), 16),
    green: Number.parseInt(value.slice(2, 4), 16),
    blue: Number.parseInt(value.slice(4, 6), 16),
  };
}

function toHex({ red, green, blue }: Rgb): string {
  return `#${[red, green, blue]
    .map((channel) => Math.round(channel).toString(16).padStart(2, "0"))
    .join("")}`;
}

function mix(first: Rgb, second: Rgb, amount: number): Rgb {
  return {
    red: first.red + (second.red - first.red) * amount,
    green: first.green + (second.green - first.green) * amount,
    blue: first.blue + (second.blue - first.blue) * amount,
  };
}

function luminance(color: Rgb): number {
  const channels = [color.red, color.green, color.blue].map((channel) => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  const [red = 0, green = 0, blue = 0] = channels;
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function contrast(first: Rgb, second: Rgb): number {
  const light = Math.max(luminance(first), luminance(second));
  const dark = Math.min(luminance(first), luminance(second));
  return (light + 0.05) / (dark + 0.05);
}

function bestForeground(background: Rgb): Rgb {
  const black = parseHex(BLACK);
  const white = parseHex(WHITE);
  return contrast(background, black) >= contrast(background, white) ? black : white;
}

function accessibleAgainst(color: Rgb, background: Rgb): Rgb {
  if (contrast(color, background) >= MINIMUM_CONTRAST) return color;
  const target =
    contrast(background, parseHex(BLACK)) > contrast(background, parseHex(WHITE))
      ? parseHex(BLACK)
      : parseHex(WHITE);
  let low = 0;
  let high = 1;
  for (let index = 0; index < 18; index += 1) {
    const midpoint = (low + high) / 2;
    if (contrast(mix(color, target, midpoint), background) >= MINIMUM_CONTRAST) {
      high = midpoint;
    } else {
      low = midpoint;
    }
  }
  return mix(color, target, high);
}

function tokens(primaryColor: string, backgroundColor: string) {
  const primary = parseHex(primaryColor);
  const background = parseHex(backgroundColor);
  const accent = accessibleAgainst(primary, background);
  const ink = bestForeground(accent);
  const hoverTarget = toHex(ink) === WHITE ? parseHex(BLACK) : parseHex(WHITE);
  return {
    accent: toHex(accent),
    hover: toHex(mix(accent, hoverTarget, 0.1)),
    deep: toHex(mix(accent, parseHex(BLACK), 0.25)),
    ink: toHex(ink),
    link: toHex(accessibleAgainst(primary, background)),
    info: toHex(mix(background, accent, 0.12)),
    focus: `${toHex(accent)}a6`,
  };
}

export function workspaceAccentStyle(
  rawPrimaryColor: string | null | undefined,
): WorkspaceAccentStyle | undefined {
  if (!rawPrimaryColor) return undefined;
  const primaryColor = WorkspacePrimaryColorSchema.parse(rawPrimaryColor);
  const light = tokens(primaryColor, LIGHT_BACKGROUND);
  const dark = tokens(primaryColor, DARK_BACKGROUND);
  return {
    "--workspace-accent-light": light.accent,
    "--workspace-accent-light-hover": light.hover,
    "--workspace-accent-light-deep": light.deep,
    "--workspace-accent-light-ink": light.ink,
    "--workspace-accent-light-link": light.link,
    "--workspace-accent-light-info": light.info,
    "--workspace-accent-light-focus": light.focus,
    "--workspace-accent-dark": dark.accent,
    "--workspace-accent-dark-hover": dark.hover,
    "--workspace-accent-dark-deep": dark.deep,
    "--workspace-accent-dark-ink": dark.ink,
    "--workspace-accent-dark-link": dark.link,
    "--workspace-accent-dark-info": dark.info,
    "--workspace-accent-dark-focus": dark.focus,
  };
}
